import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  ArrowLeft,
  FileMinus,
  Loader2,
  Send,
  Link2,
  Clock,
  CheckCircle,
  XCircle,
  Building2,
  Calendar,
  User,
  FileText,
  Receipt,
  Undo2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

const statusConfig: Record<string, { color: string; icon: React.ElementType }> = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  ISSUED: { color: 'bg-blue-100 text-blue-700', icon: Send },
  PARTIALLY_APPLIED: { color: 'bg-yellow-100 text-yellow-700', icon: Link2 },
  FULLY_APPLIED: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  CANCELLED: { color: 'bg-gray-100 text-gray-500', icon: XCircle },
};

export function CreditNoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canEdit = usePermission('CREDIT_NOTES', 'EDIT', 'TRADING');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [applicationToReverse, setApplicationToReverse] = useState<string | null>(null);

  const { data: creditNote, isLoading, error } = trpc.tradingCreditNote.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const utils = trpc.useUtils();

  const issueMutation = trpc.tradingCreditNote.issue.useMutation({
    onSuccess: () => {
      utils.tradingCreditNote.byId.invalidate(id!);
      showSuccessToast(t('finance.creditNotes.issued'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const cancelMutation = trpc.tradingCreditNote.cancel.useMutation({
    onSuccess: () => {
      utils.tradingCreditNote.byId.invalidate(id!);
      showSuccessToast(t('finance.creditNotes.cancelled'));
      setCancelDialogOpen(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const reverseApplicationMutation = trpc.tradingCreditNote.reverseApplication.useMutation({
    onSuccess: () => {
      utils.tradingCreditNote.byId.invalidate(id!);
      showSuccessToast(t('finance.creditNotes.applicationReversed'));
      setReverseDialogOpen(false);
      setApplicationToReverse(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleReverseApplication = (applicationId: string) => {
    setApplicationToReverse(applicationId);
    setReverseDialogOpen(true);
  };

  const confirmReverseApplication = () => {
    if (applicationToReverse) {
      reverseApplicationMutation.mutate(applicationToReverse);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return t('finance.creditNotes.statusDraft');
      case 'ISSUED':
        return t('finance.creditNotes.statusIssued');
      case 'PARTIALLY_APPLIED':
        return t('finance.creditNotes.statusPartiallyApplied');
      case 'FULLY_APPLIED':
        return t('finance.creditNotes.statusFullyApplied');
      case 'CANCELLED':
        return t('finance.creditNotes.statusCancelled');
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (error || !creditNote) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/trading/finance/credit-notes')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('finance.creditNotes.backToList')}
        </Button>
        <div className="border border-red-300 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{error?.message || t('finance.creditNotes.notFound')}</p>
        </div>
      </div>
    );
  }

  const config = statusConfig[creditNote.status] ?? statusConfig.DRAFT;
  const StatusIcon = config!.icon;
  const canIssue = creditNote.status === 'DRAFT';
  const canApply = ['ISSUED', 'PARTIALLY_APPLIED'].includes(creditNote.status) && creditNote.remainingCredit > 0;
  const canCancel = creditNote.status === 'DRAFT' || (creditNote.status === 'ISSUED' && creditNote.applications.length === 0);

  return (
    <div className="space-y-6">
      {/* Cancel Dialog */}
      <ConfirmationDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title={t('finance.creditNotes.cancelTitle')}
        description={t('finance.creditNotes.cancelConfirm')}
        confirmLabel={t('finance.creditNotes.cancel')}
        variant="destructive"
        onConfirm={() => cancelMutation.mutate({ id: id!, reason: 'Cancelled by user' })}
        isLoading={cancelMutation.isPending}
      />

      {/* Reverse Application Dialog */}
      <ConfirmationDialog
        open={reverseDialogOpen}
        onOpenChange={setReverseDialogOpen}
        title={t('finance.creditNotes.reverseApplicationTitle')}
        description={t('finance.creditNotes.reverseApplicationConfirm')}
        confirmLabel={t('finance.creditNotes.reverseApplication')}
        variant="destructive"
        onConfirm={confirmReverseApplication}
        isLoading={reverseApplicationMutation.isPending}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/trading/finance/credit-notes')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('finance.creditNotes.backToList')}
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center bg-purple-100">
              <FileMinus className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold font-mono">{creditNote.creditNoteNumber}</h1>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium',
                    config!.color
                  )}
                >
                  <StatusIcon className="h-3 w-3" />
                  {getStatusLabel(creditNote.status)}
                </span>
              </div>
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {canIssue && (
              <Button
                onClick={() => issueMutation.mutate(id!)}
                disabled={issueMutation.isPending}
              >
                <Send className="mr-2 h-4 w-4" />
                {t('finance.creditNotes.issue')}
              </Button>
            )}
            {canApply && (
              <Button
                variant="outline"
                onClick={() => navigate(`/trading/finance/credit-notes/${id}/apply`)}
              >
                <Link2 className="mr-2 h-4 w-4" />
                {t('finance.creditNotes.applyToInvoice')}
              </Button>
            )}
            {canCancel && (
              <Button
                variant="outline"
                onClick={() => setCancelDialogOpen(true)}
              >
                <XCircle className="mr-2 h-4 w-4" />
                {t('finance.creditNotes.cancel')}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Customer Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {t('finance.creditNotes.customer')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {creditNote.tradingCustomerId ? (
              <Link
                to={`/trading/customers/${creditNote.tradingCustomerId}`}
                className="font-medium text-primary hover:underline"
              >
                {creditNote.tradingCustomer?.companyName || creditNote.companyName}
              </Link>
            ) : (
              <span className="font-medium">{creditNote.companyName}</span>
            )}
            {creditNote.contactName && (
              <p className="text-sm text-muted-foreground">{creditNote.contactName}</p>
            )}
          </CardContent>
        </Card>

        {/* Date Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t('finance.creditNotes.creditDate')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <span className="font-medium">{formatDate(creditNote.creditDate)}</span>
          </CardContent>
        </Card>

        {/* Total Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              {t('finance.creditNotes.total')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <span className="text-xl font-bold">
              {formatCurrency(creditNote.total, creditNote.currency)}
            </span>
          </CardContent>
        </Card>

        {/* Remaining Credit Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              {t('finance.creditNotes.remaining')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <span
              className={cn(
                'text-xl font-bold',
                creditNote.remainingCredit > 0 ? 'text-blue-600' : 'text-green-600'
              )}
            >
              {formatCurrency(creditNote.remainingCredit, creditNote.currency)}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Original Invoice */}
      {creditNote.invoice && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('finance.creditNotes.originalInvoice')}
            </CardTitle>
            <CardDescription>
              {t('finance.creditNotes.creditedFromInvoice')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link
              to={`/trading/finance/invoices/${creditNote.invoiceId}`}
              className="inline-flex items-center gap-2 p-3 border border-border hover:bg-muted/50"
            >
              <Receipt className="h-4 w-4 text-muted-foreground" />
              <span className="font-mono font-medium text-primary hover:underline">
                {creditNote.invoice.invoiceNumber}
              </span>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Reason */}
      {creditNote.reason && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('finance.creditNotes.reason')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{creditNote.reason}</p>
          </CardContent>
        </Card>
      )}

      {/* Credit Note Items */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('finance.creditNotes.items')}</CardTitle>
          <CardDescription>
            {creditNote.items.length} {t('finance.creditNotes.itemCount')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.creditNotes.itemDescription')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.creditNotes.itemProduct')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('finance.creditNotes.itemQuantity')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('finance.creditNotes.itemUnitPrice')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('finance.creditNotes.itemLineTotal')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {creditNote.items.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-sm">{item.description}</td>
                    <td className="px-4 py-3 text-sm">
                      {item.productCode ? (
                        <span className="font-mono text-xs bg-muted px-1.5 py-0.5">
                          {item.productCode}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm">{item.quantity}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      {formatCurrency(item.unitPrice, creditNote.currency)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium">
                      {formatCurrency(item.lineTotal, creditNote.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/50">
                <tr>
                  <td colSpan={4} className="px-4 py-2 text-right text-sm font-medium">
                    {t('finance.creditNotes.subtotal')}
                  </td>
                  <td className="px-4 py-2 text-right text-sm font-medium">
                    {formatCurrency(creditNote.subtotal, creditNote.currency)}
                  </td>
                </tr>
                {creditNote.taxAmount > 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-2 text-right text-sm font-medium">
                      {t('finance.creditNotes.tax')}
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-medium">
                      {formatCurrency(creditNote.taxAmount, creditNote.currency)}
                    </td>
                  </tr>
                )}
                <tr className="border-t">
                  <td colSpan={4} className="px-4 py-3 text-right text-sm font-bold">
                    {t('finance.creditNotes.total')}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold">
                    {formatCurrency(creditNote.total, creditNote.currency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Credit Applications */}
      {creditNote.applications.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('finance.creditNotes.applications')}</CardTitle>
            <CardDescription>
              {t('finance.creditNotes.applicationsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border border-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('finance.creditNotes.appliedToInvoice')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('finance.creditNotes.appliedDate')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('finance.creditNotes.invoiceTotal')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('finance.creditNotes.appliedAmount')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('finance.creditNotes.appliedBy')}
                    </th>
                    {canEdit && (
                      <th className="px-4 py-3 text-center text-sm font-medium">
                        {tCommon('actions')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {creditNote.applications.map((application) => (
                    <tr key={application.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm">
                        <Link
                          to={`/trading/finance/invoices/${application.invoiceId}`}
                          className="font-mono font-medium text-primary hover:underline"
                        >
                          {application.invoiceNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatDate(application.appliedDate)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        {formatCurrency(application.invoiceTotal, creditNote.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-green-600">
                        {formatCurrency(application.amount, creditNote.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {application.createdBy || '-'}
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReverseApplication(application.id)}
                          >
                            <Undo2 className="mr-1 h-3 w-3" />
                            {t('finance.creditNotes.reverse')}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/50">
                  <tr>
                    <td colSpan={3} className="px-4 py-2 text-right text-sm font-medium">
                      {t('finance.creditNotes.totalApplied')}
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-bold text-green-600">
                      {formatCurrency(creditNote.appliedAmount, creditNote.currency)}
                    </td>
                    <td colSpan={canEdit ? 2 : 1}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('finance.creditNotes.metadata')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {creditNote.createdBy && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <span className="text-sm text-muted-foreground">
                    {t('finance.creditNotes.createdBy')}
                  </span>
                  <p className="font-medium">
                    {creditNote.createdBy.firstName} {creditNote.createdBy.lastName}
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-sm text-muted-foreground">
                  {t('finance.creditNotes.createdAt')}
                </span>
                <p className="font-medium">{formatDate(creditNote.createdAt)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-sm text-muted-foreground">
                  {t('finance.creditNotes.updatedAt')}
                </span>
                <p className="font-medium">{formatDate(creditNote.updatedAt)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
